import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  ArrowRight,
  BookOpen,
  Library,
  GraduationCap,
  FolderInput,
  Archive,
  Newspaper,
  ScrollText,
  Building2,
  Quote,
} from "lucide-react";
import api from "@/lib/api";

const DEFAULTS = {
  hero: {
    eyebrow: "SECRÉTARIAT GÉNÉRAL À LA RECHERCHE",
    title_left: "Bienvenue sur",
    title_highlight: "UNISIC-Recherche",
    subtitle:
      "Portail officiel dédié à la recherche, à la valorisation des travaux scientifiques et à la promotion de la science ouverte à l'Université des Sciences de l'Information et de la Communication.",
    text_intro: "",
    image_url:
      "/media/modern-library.jpg",
    cta_primary_label: "Connexion",
    cta_primary_link: "/connexion",
    cta_secondary_label: "Découvrir les Directions",
    cta_secondary_link: "#directions",
  },
  sg: {
    eyebrow: "Éditorial",
    title: "Mot de la Rectrice",
    quote:
      "La recherche universitaire est le moteur silencieux qui transforme la connaissance en action.",
    body: "",
    author_name: "Professeure Ordinaire Espérance BAYEDILA BAKANDA",
    author_title: "Rectrice de l'UNISIC",
    photo_url: "/rectrice-bayedila.jpg",
  },
};

const SERVICE_KEYS = {
  "/directions/bibliotheques-musees": "direction.bibliotheques_musees",
  "/directions/ecole-doctorale": "service.ecole_doctorale",
  "/directions/suivi-encadrement": "direction.suivi_encadrement",
};

const services = [
  {
    to: "/directions/bibliotheques-musees",
    label: "Direction de la Recherche, des Bibliothèques et Musées",
    text: "Recherche scientifique, conservation et valorisation des ressources documentaires, éditoriales et patrimoniales.",
    Icon: Library,
    image: "/media/library-shelves.jpg",
    accent: "#0A2E52",
  },
  {
    to: "/directions/ecole-doctorale",
    label: "École Doctorale",
    text: "Formation, encadrement et accompagnement des chercheurs doctorants.",
    Icon: GraduationCap,
    image: "/media/hero-university.jpg",
    accent: "#D4AF37",
  },
  {
    to: "/directions/suivi-encadrement",
    label: "Direction de Suivi et Encadrement du Personnel Académique",
    text: "Gestion de carrière et encadrement du personnel académique et scientifique.",
    Icon: Building2,
    image: "/media/research-lab.jpg",
    accent: "#144C86",
  },
];

const platforms = [
  {
    to: "/plateformes/depots",
    label: "Dépôts des travaux académiques",
    text: "Soumettre en ligne un mémoire ou un projet tutoré et suivre son évaluation.",
    Icon: FolderInput,
    accent: "#E63946",
  },
  {
    to: "/plateformes/archives-academiques",
    label: "Dépôt institutionnel",
    text: "Rechercher et consulter tous les travaux scientifiques validés de l'université.",
    Icon: Archive,
    accent: "#0A2E52",
  },
  {
    to: "/plateformes/revues",
    label: "Les Cahiers Congolais de Communication",
    text: "La revue scientifique du CECOM. Objectifs, comité éditorial et accès au site officiel.",
    Icon: BookOpen,
    accent: "#144C86",
  },
];

export default function Home() {
  const [news, setNews] = useState([]);
  const [content, setContent] = useState(DEFAULTS);
  const [serviceBanners, setServiceBanners] = useState({});

  useEffect(() => {
    api.get("/news?limit=3").then((r) => setNews(r.data)).catch(() => {});
    api
      .get("/content/home")
      .then((r) => {
        const data = r.data || {};
        setContent({
          hero: { ...DEFAULTS.hero, ...(data.hero || {}) },
          sg: { ...DEFAULTS.sg, ...(data.sg || {}) },
        });
      })
      .catch(() => {});
    // Fetch each service banner so the home cards mirror the service page banners
    Object.entries(SERVICE_KEYS).forEach(([route, key]) => {
      api
        .get(`/content/${key}`)
        .then((r) => {
          const img = r.data?.banner_image;
          if (img) setServiceBanners((prev) => ({ ...prev, [route]: img }));
        })
        .catch(() => {});
    });
  }, []);

  const { hero, sg } = content;

  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-[92vh] w-full overflow-hidden" data-testid="home-hero">
        <img
          src={hero.image_url}
          alt="Bibliothèque UNISIC"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 hero-veil" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 h-full py-32 lg:py-40 flex flex-col justify-center">
          <div className="max-w-3xl">
            <div className="eyebrow" style={{ color: "#E5C158" }}>
              {hero.eyebrow}
            </div>
            <h1 className="mt-4 text-white text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
              {hero.title_left}{" "}
              <span className="text-unisic-gold">{hero.title_highlight}</span>
            </h1>
            <p className="mt-6 text-white/85 text-lg max-w-2xl font-light">{hero.subtitle}</p>
            {hero.text_intro && (
              <p className="mt-4 text-white/70 max-w-2xl text-sm">{hero.text_intro}</p>
            )}
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to={hero.cta_primary_link || "/connexion"} className="btn-primary" data-testid="hero-cta-login">
                {hero.cta_primary_label || "Connexion"}
                <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href={hero.cta_secondary_link || "#services"}
                className="btn-outline-white"
                data-testid="hero-cta-services"
              >
                {hero.cta_secondary_label || "Découvrir les Directions"}
              </a>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-unisic-gold" />
      </section>

      {/* MOT DU SECRÉTAIRE */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-24" data-testid="section-mot-sg">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-4">
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-24 h-24 bg-unisic-gold" />
              <img
                src={sg.photo_url}
                alt="Rectrice de l'UNISIC"
                className="relative w-full aspect-[4/5] object-cover"
              />
            </div>
          </div>
          <div className="lg:col-span-8">
            <div className="eyebrow eyebrow-dark">{sg.eyebrow}</div>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-unisic-blue">{sg.title}</h2>
            <span className="divider-gold mt-4" />
            <div className="mt-6 relative pl-8">
              <Quote className="absolute -top-2 left-0 w-6 h-6 text-unisic-gold" />
              <p className="font-serif-quote italic text-lg text-slate-700 leading-relaxed">
                « {sg.quote} »
              </p>
              {sg.body && (
                <p className="mt-6 text-sm text-slate-600 leading-relaxed whitespace-pre-line">
                  {sg.body}
                </p>
              )}
              <div className="mt-6">
                <div className="font-display font-semibold text-unisic-blue">{sg.author_name}</div>
                <div className="text-xs uppercase tracking-widest text-slate-500 mt-1">
                  {sg.author_title}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* DIRECTIONS */}
      <section
        id="directions"
        className="bg-slate-50 border-y border-slate-200"
        data-testid="section-directions"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-24">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
            <div>
              <div className="eyebrow eyebrow-dark">Niveau 1</div>
              <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-unisic-blue">
                Les Directions du Secrétariat Général à la Recherche
              </h2>
              <span className="divider-gold mt-4" />
              <p className="mt-4 text-slate-600 max-w-2xl">
                Trois grandes Directions structurent l'activité scientifique de l'UNISIC : la
                Direction de la Recherche, des Bibliothèques et Musées, l'École Doctorale et la
                Direction de Suivi et Encadrement du Personnel Académique et Scientifique. Chacune
                dispose de son propre espace, poursuit une mission spécifique et rend compte de
                ses activités.
              </p>
            </div>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            {services.map((s) => (
              <Link
                to={s.to}
                key={s.to}
                className="paper-card card-hover overflow-hidden group flex flex-col"
                data-testid={`service-card-${s.to.split("/").pop()}`}
              >
                <div className="relative h-44 overflow-hidden">
                  <img src={serviceBanners[s.to] || s.image} alt={s.label} className="w-full h-full object-cover" />
                  <div
                    className="absolute inset-0 mix-blend-multiply opacity-30"
                    style={{ background: s.accent }}
                  />
                  <div className="absolute top-0 left-0 right-0 h-1" style={{ background: s.accent }} />
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <div className="flex items-center gap-3 mb-3">
                    <div
                      className="p-2 rounded-sm"
                      style={{ background: `${s.accent}14`, color: s.accent }}
                    >
                      <s.Icon className="w-5 h-5" />
                    </div>
                    <div className="text-[11px] uppercase tracking-widest text-slate-500">
                      Direction
                    </div>
                  </div>
                  <h3 className="font-display font-semibold text-unisic-blue text-lg leading-snug">
                    {s.label}
                  </h3>
                  <p className="mt-3 text-sm text-slate-600 flex-1">{s.text}</p>
                  <div className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-unisic-blue group-hover:text-unisic-red transition-colors">
                    Accéder <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* PLATFORMS */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-24" data-testid="section-platforms">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
          <div>
            <div className="eyebrow eyebrow-dark">Niveau 2</div>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-unisic-blue">
              Les plateformes numériques
            </h2>
            <span className="divider-gold mt-4" />
            <p className="mt-4 text-slate-600 max-w-2xl">
              Applications au service de la recherche : dépôts en ligne, archives, publications.
            </p>
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {platforms.map((p) => (
            <Link
              to={p.to}
              key={p.to}
              className="paper-card card-hover p-6 flex flex-col"
              data-testid={`platform-card-${p.to.split("/").pop()}`}
            >
              <div
                className="w-11 h-11 rounded-sm flex items-center justify-center mb-4"
                style={{ background: `${p.accent}18`, color: p.accent }}
              >
                <p.Icon className="w-5 h-5" />
              </div>
              <div
                className="text-[11px] font-bold uppercase tracking-widest mb-2"
                style={{ color: p.accent }}
              >
                Plateforme
              </div>
              <h3 className="font-display font-semibold text-unisic-blue text-lg leading-snug">
                {p.label}
              </h3>
              <p className="mt-3 text-sm text-slate-600 flex-1">{p.text}</p>
              <div className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-unisic-blue group-hover:text-unisic-red transition-colors">
                Accéder <ArrowRight className="w-4 h-4" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* NEWS */}
      <section className="bg-slate-50 border-t border-slate-200" data-testid="section-news">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-24">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
            <div>
              <div className="eyebrow eyebrow-dark">
                <Newspaper className="inline w-3.5 h-3.5 -mt-1 mr-1" />
                Actualités
              </div>
              <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-unisic-blue">
                Dernières actualités de la recherche
              </h2>
              <span className="divider-gold mt-4" />
            </div>
            <Link to="/actualites" className="link-underline" data-testid="news-see-all">
              Voir toutes les actualités
            </Link>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            {news.length === 0 && (
              <div className="col-span-3 text-center text-slate-500 text-sm py-10">
                Les actualités seront bientôt disponibles.
              </div>
            )}
            {news.map((n) => (
              <Link
                to={`/actualites/${n.id}`}
                key={n.id}
                className="paper-card card-hover overflow-hidden flex flex-col"
                data-testid={`news-card-${n.id}`}
              >
                {n.image_url && (
                  <div className="h-44 overflow-hidden">
                    <img src={n.image_url} alt={n.title} className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="p-6 flex-1 flex flex-col">
                  <div className="eyebrow eyebrow-dark text-[10px]">{n.category || "Actualité"}</div>
                  <h3 className="mt-2 font-display font-semibold text-unisic-blue text-lg leading-snug">
                    {n.title}
                  </h3>
                  <p className="mt-3 text-sm text-slate-600 flex-1 line-clamp-3">{n.summary}</p>
                  <div className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-unisic-red">
                    Lire <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
